import { useState, useEffect } from 'react'
import { api, formatDate } from '../api'
import RinaContextCard from '../components/RinaContextCard'
import TourneeCheckout from '../components/TourneeCheckout'
import CatalogueScreen from '../components/paniers/CatalogueScreen'
import ProduitScreen from '../components/paniers/ProduitScreen'
import useTourneeCart from '../hooks/useTourneeCart'
import WalletIcon from '../components/WalletIcon'
import { getCached, setCached, useCachedFetch } from '../lib/swr'
import { ouvrirProfil } from '../components/ProfileSheet'
import FondPage from '../components/FondPage'
import EnteteModule from '../components/EnteteModule'
import BarreOnglets from '../components/BarreOnglets'
import { useTheme } from '../theme'
import { useAuth } from '../AuthContext'
import { useActionsPortail } from '../contexts/navigation'
import { avecAlpha } from '../lib/couleurs'

// Module Paniers (tournees, client) — ECRAN PLEIN fidele prototype v5 (§5, ScreenTournee/Rayons/Panier).
// inc.3 (catalogue + panier + Mes commandes) sur les endpoints existants. Le checkout/paiement (inc.4)
// est branche sur `POST /client/orders` mais l'ecran Finaliser attend l'activation paiement (cf. note).

const ORANGE = 'rgb(var(--c-primary))'
const TEAL = 'rgb(var(--c-teal))'
const VERT = 'rgb(var(--c-green))'
const formatPrix = (n) => `${Number(n).toFixed(2).replace('.', ',')} €`

function SectionLabel({ children, color = ORANGE }) {
  return <p className="text-[11px] font-bold uppercase tracking-[0.16em] mb-2.5 mx-0.5" style={{ color }}>{children}</p>
}

const STATUT_LABEL = {
  EN_ATTENTE: { l: 'En attente', c: 'rgba(99,102,241,0.16)', t: '#4b4fb0' },
  CONFIRMEE: { l: 'Confirmée', c: 'rgba(20,184,166,0.18)', t: '#0a6b62' },
  EN_PREPARATION: { l: 'En préparation', c: 'rgba(20,184,166,0.18)', t: '#0a6b62' },
  PRETE: { l: 'Prête', c: 'rgba(20,184,166,0.18)', t: '#0a6b62' },
  LIVREE: { l: 'Livrée', c: 'rgba(7,62,61,0.1)', t: VERT },
  ANNULEE: { l: 'Annulée', c: 'rgba(7,62,61,0.1)', t: 'rgba(7,62,61,0.6)' },
}

export default function PaniersModule() {
  // Autonome (S48) : auth et navigation depuis les contextes.
  const { isAuthenticated } = useAuth()
  const { fermerModule, navigate: onNavigate } = useActionsPortail()
  const onExit = () => fermerModule()
  const onOpenWallet = () => { fermerModule('wallet'); window.scrollTo(0, 0) }
  const { isWeb3 } = useTheme()
  const tc = 'text-ink'
  const ts = 'text-ink/60'
  const glass = 'panneau-verre'

  const cart = useTourneeCart()

  // Pastille wallet du header (proto v7.2.1) : solde client (facette client du module).
  const { data: walletData } = useCachedFetch('wallet/me', api.getWallet, { enabled: !!isAuthenticated })
  const walletSolde = Number(walletData?.solde?.client) || 0
  const walletSoldeTxt = (walletSolde < 0 ? '−' : '') + Math.abs(walletSolde).toFixed(2).replace('.', ',') + '€'

  const TOURNEES_KEY = '/public/tournees'
  const cachedTournees = getCached(TOURNEES_KEY, { persist: true })
  const [tournees, setTournees] = useState(cachedTournees ?? [])
  const [loadingTournees, setLoadingTournees] = useState(cachedTournees === undefined)
  const [tour, setTour] = useState(null)
  const [detail, setDetail] = useState(null)
  const [loadingDetail, setLoadingDetail] = useState(false)
  const [screen, setScreen] = useState('tournees') // tournees | rayons | produit | panier | finaliser | commandes
  const [rayon, setRayon] = useState('Tout')
  const [produit, setProduit] = useState(null)
  const [orders, setOrders] = useState(null)

  // Chargement des tournees ouvertes. On reste TOUJOURS sur l'etape 1 (choix de
  // la tournee, fidele prototype v5) — meme s'il n'y en a qu'une, le client choisit.
  useEffect(() => {
    api.getTournees()
      .then((d) => {
        const list = Array.isArray(d) ? d : []
        setCached(TOURNEES_KEY, list, { persist: true })
        setTournees(list)
      })
      .catch(() => setTournees((prev) => prev))
      .finally(() => setLoadingTournees(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const selectTour = (t) => {
    setTour(t)
    setRayon('Tout')
    setScreen('rayons')
    // Detail en cache memoire : reentrer dans une tournee deja vue est instantane.
    const key = `/public/tournees/${t.id}`
    const cachedDetail = getCached(key)
    setDetail(cachedDetail ?? null)
    setLoadingDetail(cachedDetail === undefined)
    api.getTourneeDetail(t.id)
      .then((d) => { setCached(key, d); setDetail(d) })
      .catch(() => setDetail((prev) => prev ?? { produits: [] }))
      .finally(() => setLoadingDetail(false))
  }

  const openProduit = (p) => { setProduit(p); setScreen('produit') }

  // Mes commandes (au 1er affichage de l'onglet).
  useEffect(() => {
    if (screen === 'commandes' && isAuthenticated && orders === null) {
      api.getClientOrders().then((d) => setOrders(Array.isArray(d) ? d : [])).catch(() => setOrders([]))
    }
  }, [screen, isAuthenticated, orders])

  const produits = detail?.produits || []

  // Map tourOfferId -> { produit, offre } pour rendre le panier.
  const offerMap = {}
  produits.forEach((p) => (p.offres || []).forEach((o) => { offerMap[o.tourOfferId] = { produit: p, offre: o } }))

  const cartEntries = tour ? Object.entries(cart.cartFor(tour.id)) : []
  const cartItems = cartEntries
    .map(([offerId, q]) => ({ offerId, q, ...offerMap[offerId] }))
    .filter((it) => it.produit)
  const sousTotal = cartItems.reduce((s, it) => s + Number(it.offre.prix) * it.q, 0)
  const cartCount = tour ? cart.countFor(tour.id) : 0

  const rinaMsg = screen === 'panier' ? 'Votre panier. Ajustez les quantités, je calcule le total.'
    : screen === 'commandes' ? 'Vos commandes et leur suivi. Un souci ? Je suis là.'
    : screen === 'tournees' ? 'Choisissez votre tournée de livraison — je vous montre les produits dispo.'
    : screen === 'produit' ? 'Qui produit ? À quel prix ? Tout est là — ajoutez ce qu’il vous faut.'
    : 'Parcourez les rayons. Touchez un produit pour l’ajouter à votre panier.'

  const tabs = [
    { k: 'tournees', e: '🚚', l: 'Tournées' },
    { k: 'rayons', e: '🥭', l: 'Rayons' },
    { k: 'panier', e: '🧺', l: 'Panier' },
    { k: 'commandes', e: '📦', l: 'Commandes' },
  ]

  const goTab = (k) => {
    if (k === 'rayons' && !tour) { setScreen('tournees'); return }
    setScreen(k)
  }

  // Bouton retour du header : remonte d'un cran dans le parcours (fil d'Ariane),
  // puis sort du module (Lyannaj) depuis l'etape 1.
  const headerBack = () => {
    if (screen === 'produit') { setScreen('rayons'); return }
    if (screen === 'finaliser') { setScreen('panier'); return }
    if (screen === 'panier' || screen === 'commandes') { setScreen(tour ? 'rayons' : 'tournees'); return }
    if (screen === 'rayons') { setTour(null); setScreen('tournees'); return }
    onExit()
  }

  return (
    <FondPage halos>

      <div className="pb-28 max-w-md mx-auto relative z-[2]">
        {/* Header module */}
        <EnteteModule
          retour={{ label: screen === 'tournees' ? 'Lyannaj' : 'Retour', onClick: headerBack }}
          titre={`🧺 ${tour && screen !== 'tournees' ? (tour.nom || 'Paniers') : 'Paniers'}`}
        >
          {isAuthenticated && (
            <button onClick={onOpenWallet} className={`flex items-center gap-1.5 rounded-full px-2.5 py-2 flex-shrink-0 text-[11px] font-bold ${glass} ${tc}`}>
              <WalletIcon size={14} color={isWeb3 ? 'rgb(var(--c-creme))' : VERT} /> {walletSoldeTxt}
            </button>
          )}
          <button onClick={() => (isAuthenticated ? ouvrirProfil() : onNavigate('connexion'))} className={`flex items-center gap-1.5 rounded-full pl-2 pr-3 py-1.5 flex-shrink-0 ${glass}`} style={{ border: `1.5px solid ${ORANGE}` }}>
            <span className="w-7 h-7 rounded-full flex items-center justify-center text-sm" style={{ background: avecAlpha(ORANGE, 0.2) }}>{isAuthenticated ? '🧑🏾' : '👤'}</span>
            <span className="text-xs font-bold" style={{ color: isWeb3 ? '#fff' : VERT }}>{isAuthenticated ? 'Client' : 'Se connecter'}</span>
          </button>
        </EnteteModule>

        <div className="px-4 pt-1">
          <RinaContextCard message={rinaMsg} />

          {/* ===== TOURNEES (selection) ===== */}
          {screen === 'tournees' && (
            loadingTournees ? (
              <div className="space-y-3">{[1, 2].map(i => <div key={i} className={`h-24 rounded-2xl animate-pulse bg-ink/5`} />)}</div>
            ) : tournees.length === 0 ? (
              <div className="text-center pt-8 animate-fade-up">
                <span className="text-4xl block mb-2">🧺</span>
                <p className={`text-sm font-semibold ${tc}`}>Aucune tournée ouverte</p>
                <p className={`text-xs mt-1 ${ts}`}>Les prochaines tournées apparaîtront ici dès leur ouverture.</p>
              </div>
            ) : (
              <>
                <p className="text-[10.5px] font-bold uppercase tracking-[0.22em] mt-1" style={{ color: ORANGE }}>Étape 1</p>
                <h1 className={`font-serif text-[28px] leading-tight mt-1.5 mb-4 ${tc}`}>
                  Choisissez votre<br /><em style={{ color: ORANGE }}>tournée de livraison</em>
                </h1>
                <div className="flex flex-col gap-3">
                  {tournees.map((t, i) => (
                    <button key={t.id} onClick={() => selectTour(t)} style={{ animationDelay: `${i * 0.05}s`, border: `1.5px solid ${ORANGE}` }}
                      className={`text-left rounded-[22px] p-4 animate-fade-up active:scale-[0.98] transition-transform ${glass}`}>
                      <div className="flex items-center justify-between gap-2">
                        <p className={`font-serif text-[20px] ${tc}`}>{t.nom || (t.dateLivraison ? `Tournée du ${formatDate(t.dateLivraison)}` : 'Tournée')}</p>
                        <span className="text-[9.5px] font-bold tracking-wide px-2.5 py-1 rounded-full" style={{ background: TEAL, color: '#04302f' }}>OUVERTE</span>
                      </div>
                      <p className={`text-[12.5px] mt-1.5 ${ts}`}>
                        {t.dateClotureCommandes
                          ? <>⏳ Commandez avant le <strong>{formatDate(t.dateClotureCommandes)}</strong></>
                          : t.dateLivraison ? <>📦 Livraison {formatDate(t.dateLivraison)}</> : 'Commandes ouvertes'}
                      </p>
                      {t.nbProduits != null && <p className="text-[11px] mt-1 font-bold" style={{ color: ORANGE }}>{t.nbProduits} produit{t.nbProduits > 1 ? 's' : ''} disponible{t.nbProduits > 1 ? 's' : ''} →</p>}
                    </button>
                  ))}
                </div>
              </>
            )
          )}

          {/* ===== RAYONS (catalogue) ===== */}
          {screen === 'rayons' && tour && (
            <CatalogueScreen
              glass={glass}
              tc={tc}
              ts={ts}
              tour={tour}
              produits={produits}
              paniers={detail?.paniers || []}
              loadingDetail={loadingDetail}
              rayon={rayon}
              setRayon={setRayon}
              cart={cart}
              formatDate={formatDate}
              onOpenProduit={openProduit}
            />
          )}

          {/* ===== FICHE PRODUIT ===== */}
          {screen === 'produit' && tour && (
            <ProduitScreen
              glass={glass}
              tc={tc}
              ts={ts}
              tour={tour}
              produit={produit}
              cart={cart}
              formatDate={formatDate}
              onBack={() => setScreen('rayons')}
              onGoPanier={() => setScreen('panier')}
            />
          )}

          {/* ===== PANIER ===== */}
          {screen === 'panier' && (
            cartItems.length === 0 ? (
              <div className={`rounded-[24px] p-8 text-center animate-fade-up ${glass}`}>
                <div className="text-5xl">🧺</div>
                <p className={`font-bold mt-2 ${tc}`}>Votre panier est vide</p>
                <button onClick={() => setScreen(tour ? 'rayons' : 'tournees')} className="mt-4 px-6 py-3 rounded-full text-[13.5px] font-extrabold" style={{ background: ORANGE, color: VERT }}>Voir les rayons</button>
              </div>
            ) : (
              <>
                <SectionLabel color={TEAL}>Votre panier ({cartCount})</SectionLabel>
                <div className="flex flex-col gap-2.5">
                  {cartItems.map((it) => (
                    <div key={it.offerId} className={`flex items-center gap-3 px-3.5 py-3 rounded-[20px] ${glass}`} style={{ border: `1px solid ${avecAlpha(TEAL, 0.33)}` }}>
                      <span className="text-[28px]">{it.produit.product?.category?.emoji || '🧺'}</span>
                      <div className="flex-1 min-w-0">
                        <p className={`text-[13.5px] font-bold truncate ${tc}`}>{it.produit.product?.nom}</p>
                        <p className={`text-[11px] ${ts}`}>{formatPrix(it.offre.prix)} · {it.offre.producteur?.nom || 'Producteur'}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <button onClick={() => cart.add(tour.id, it.offerId, -1)} className={`w-8 h-8 rounded-[10px] font-bold ${glass} ${tc}`}>−</button>
                        <span className={`font-bold text-sm w-5 text-center ${tc}`}>{it.q}</span>
                        <button onClick={() => cart.add(tour.id, it.offerId, 1)} className="w-8 h-8 rounded-[10px] font-bold" style={{ background: ORANGE, color: VERT }}>+</button>
                      </div>
                    </div>
                  ))}
                </div>

                <div className={`rounded-[22px] p-4 mt-3 ${glass}`}>
                  <div className="flex items-center justify-between">
                    <span className={`text-sm ${ts}`}>Sous-total</span>
                    <span className={`text-base font-extrabold ${tc}`}>{formatPrix(sousTotal)}</span>
                  </div>
                  <p className={`text-[11px] mt-1 ${ts}`}>Frais de retrait et avoirs calculés à l'étape suivante.</p>
                </div>

                <button onClick={() => setScreen('finaliser')} className="mt-4 w-full py-4 rounded-full text-[15px] font-extrabold text-creme" style={{ background: VERT, boxShadow: '0 14px 32px rgba(7,62,61,0.3)' }}>
                  Finaliser la commande
                </button>
              </>
            )
          )}

          {/* ===== FINALISER (inc.4 — checkout + paiement) ===== */}
          {screen === 'finaliser' && tour && (
            <TourneeCheckout
              tour={tour}
              items={cartItems}
              sousTotal={sousTotal}
              isAuthenticated={isAuthenticated}
              onNavigate={onNavigate}
              onBack={() => setScreen('panier')}
              onPlaced={() => cart.clear(tour.id)}
              onGoCommandes={() => { setOrders(null); setScreen('commandes') }}
            />
          )}

          {/* ===== MES COMMANDES ===== */}
          {screen === 'commandes' && (
            !isAuthenticated ? (
              <div className={`rounded-3xl p-7 text-center mt-1 animate-fade-up ${glass}`}>
                <span className="text-4xl block mb-2">🧾</span>
                <p className="font-serif text-[20px] mb-1.5" style={{ color: isWeb3 ? '#fff' : VERT }}>Vos commandes, au même endroit</p>
                <p className={`text-[13px] mb-4 ${ts}`}>Créez un compte pour suivre vos commandes et leur livraison.</p>
                <button onClick={() => onNavigate?.('connexion')} className="px-7 py-3 rounded-full text-white text-sm font-extrabold" style={{ background: 'linear-gradient(120deg, rgba(244,137,83,0.9), rgba(224,101,58,0.92))' }}>Créer un compte</button>
              </div>
            ) : orders === null ? (
              <div className="space-y-2">{[1, 2].map(i => <div key={i} className={`h-20 rounded-2xl animate-pulse bg-ink/5`} />)}</div>
            ) : orders.length === 0 ? (
              <div className="text-center pt-8 animate-fade-up">
                <span className="text-4xl block mb-2">🧾</span>
                <p className={`text-sm font-semibold ${tc}`}>Aucune commande</p>
                <p className={`text-xs mt-1 ${ts}`}>Vos commandes de tournée apparaîtront ici.</p>
              </div>
            ) : (
              <>
                <SectionLabel>Mes commandes ({orders.length})</SectionLabel>
                <div className="flex flex-col gap-2.5">
                  {orders.map((o) => {
                    const st = STATUT_LABEL[o.statut] || STATUT_LABEL.EN_ATTENTE
                    return (
                      <div key={o.id} className={`rounded-[20px] p-4 ${glass}`}>
                        <div className="flex items-center justify-between">
                          <p className={`text-[13.5px] font-bold ${tc}`}>{o.tour?.nom || o.visibleId || 'Commande'}</p>
                          <span className="text-[9.5px] font-bold px-2.5 py-1 rounded-full" style={{ background: st.c, color: st.t }}>{st.l}</span>
                        </div>
                        <p className={`text-[11.5px] mt-1 ${ts}`}>
                          {formatPrix(o.total)} · {(o.items?.length || 0)} produit{(o.items?.length || 0) > 1 ? 's' : ''}
                          {o.paiementStatut === 'PAYE' ? ' · payée' : ''}
                        </p>
                      </div>
                    )
                  })}
                </div>
              </>
            )
          )}
        </div>
      </div>

      {/* Bottom-nav module (Tournees / Rayons / Panier / Commandes, fidele proto v5) — masquee seulement au checkout */}
      {screen !== 'finaliser' && (
        <BarreOnglets
          onglets={tabs.map((t) => (t.k === 'panier' ? { ...t, badge: cartCount } : t))}
          actif={screen === 'produit' ? 'rayons' : screen}
          onSelect={goTab}
        />
      )}
    </FondPage>
  )
}
